import ValidateModule from "./ValidateModule";

import type { IEventChannel, ApiOpts } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";

const es = global.es;

/**
 * @class UserSettingsModule
 */
export default class UserSettingsModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.get = this.get.bind(this);
        this.update = this.update.bind(this);
        this.generateApiKey = this.generateApiKey.bind(this);
        this.getAllUserAddresses = this.getAllUserAddresses.bind(this);
        this.getUserAddresses = this.getUserAddresses.bind(this);
        this.addUserAddress = this.addUserAddress.bind(this);
        this.updateUserAddress = this.updateUserAddress.bind(this);
        this.removeUserAddress = this.removeUserAddress.bind(this);
    }


    /**
     * Get the resource from the server
     * 
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the projections are loaded
     */
    get(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName);
        //return rqBroker.doGet({url: url});
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "UserSettings", opts);
    }
    
    /**
     * PUT a new resource to the server (note: id attrib must be valid)
     * 
     * @param resourceObj e.g.
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    update(resourceObj: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName);
        
        return this.rqBroker.doPut({
            url: url,
            data: resourceObj,
            opts: opts
        }).done((rs) => {
            this.cache?.clear("UserSettings");
        });
    }
    
    /**
     * Generate a new API key
     * 
     * @param opts 
     * 
     * @returns 
     */
    generateApiKey(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/api-key";

        return this.rqBroker.doPost({
            url: url,
            data: null,
            opts: opts
        }).done((rs) => {
            this.cache?.clear("UserSettings");
        });
    }

    /**
     * 
     * @param addressType 
     * @param opts 
     * 
     * @returns 
     */
    getAllUserAddresses(addressType: string, opts?: ApiOpts) {
        return this.getUserAddresses(addressType, null, opts);
    }

    /**
     * 
     * @param addressType 
     * @param searchString 
     * @param opts 
     * 
     * @returns 
     */
    getUserAddresses(addressType: string, searchString?: string, opts?: ApiOpts) {
        let url = es.api.getURL(this.resourceName) + "/userAddress/search";
        let added = false;
        if (addressType) {
            url += (added ? '&' : '?') + 'addressType=' + encodeURIComponent(addressType);
            added = true;
        }
        if (searchString) {
            url += '?searchString=' + encodeURIComponent(searchString);
            added = true;
        }

        return this.rqBroker.doGet({
            url: url,
            opts: opts
        });
    }

    /**
     * 
     * @param addressType 
     * @param address 
     * @param location 
     * @param opts 
     * 
     * @returns 
     */
    addUserAddress(addressType: string, address: string, location: number[], opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/userAddress";
        const data = {
            'addressType': addressType,
            'address': address,
            'location': location
        };
        
        return this.rqBroker.doPost({
            url: url,
            data: data,
            opts: opts
        });
    }

    /**
     * 
     * @param userAddress 
     * @param opts 
     * 
     * @returns 
     */
    updateUserAddress(userAddress: object, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/userAddress/" + userAddress.id;
        
        return this.rqBroker.doPut({
            url: url,
            data: userAddress,
            opts: opts
        });
    }

    /**
     * 
     * @param addressId 
     * @param opts 
     * 
     * @returns 
     */
    removeUserAddress(addressId: number, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/userAddress/" + addressId;

        return this.rqBroker.doDelete({
            url: url,
            opts: opts
        });
    }
}
